import classNames from 'clsx';
import React, { useEffect, useRef } from 'react';

import useElementEdgeFade from '@swf/core/hooks/useElementEdgeFade';
import { canUseDOM } from '@swf/core/utils';
import { Icon } from '@swf/sports/components';

import type {
  PolymorphicForwardRefExoticComponent,
  PolymorphicPropsWithRef,
  PolymorphicPropsWithoutRef
} from '../../types';

import styles from './ScrollableList.scss';

const TabDefaultElement = 'button';
interface TabOwnProps {
  active?: boolean;
}
type TabProps<T extends React.ElementType = typeof TabDefaultElement> = PolymorphicPropsWithRef<
  TabOwnProps,
  T
>;

export const Tab = <T extends React.ElementType = typeof TabDefaultElement>({
  as,
  children,
  className,
  role = 'tab',
  active = false,
  ...props
}: TabProps<T>) => {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (active && ref.current && canUseDOM) {
      const rect = ref.current.getBoundingClientRect();

      if (
        rect.top >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      ) {
        ref.current.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: 'smooth'
        });
      }
    }
  }, [active]);
  const Element: React.ElementType = as ?? TabDefaultElement;

  return (
    <li ref={ref} className={className}>
      <Element
        {...props}
        role={role}
        className={classNames(styles.tab, { [styles.activeTab]: active })}
        aria-selected={active}
      >
        {children}
      </Element>
    </li>
  );
};

const PillDefaultElement = 'button';
interface PillOwnProps {
  active?: boolean;
}
type PillProps<T extends React.ElementType = typeof PillDefaultElement> = PolymorphicPropsWithRef<
  PillOwnProps,
  T
>;

export const Pill = <T extends React.ElementType = typeof PillDefaultElement>({
  as,
  children,
  className,
  role = 'tab',
  active = false,
  ...props
}: PillProps<T>) => {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (active && ref.current && canUseDOM) {
      const rect = ref.current.getBoundingClientRect();

      if (
        rect.top >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      ) {
        ref.current.scrollIntoView({
          block: 'nearest',
          inline: 'center',
          behavior: 'smooth'
        });
      }
    }
  }, [active]);
  const Element: React.ElementType = as ?? PillDefaultElement;

  return (
    <li ref={ref} className={className}>
      <Element
        {...props}
        role={role}
        className={classNames(styles.pill, { [styles.activePill]: active })}
        aria-selected={active}
      >
        {children}
      </Element>
    </li>
  );
};

// Magic number is total width of button, gradient & wrapper padding
const SCROLLBY_ADJUSTMENT = 64;

const ScrollableListDefaultElement = 'nav';
interface ScrollableListOwnProps {
  variant?: 'default' | 'square-scroll-arrows';
}

export const ScrollableList: PolymorphicForwardRefExoticComponent<
  ScrollableListOwnProps,
  typeof ScrollableListDefaultElement
> = React.forwardRef(
  <T extends React.ElementType = typeof TabDefaultElement>(
    {
      as,
      role = 'tablist',
      children,
      variant = 'default'
    }: PolymorphicPropsWithoutRef<ScrollableListOwnProps, T>,
    ref?: React.ForwardedRef<Element>
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const listContainerRef = useRef<HTMLDivElement>(null);

    useElementEdgeFade(scrollRef, listContainerRef);

    const Element: React.ElementType = as ?? ScrollableListDefaultElement;
    return (
      <Element
        ref={listContainerRef}
        className={classNames(styles.container, {
          [styles.squareScrollArrows]: variant === 'square-scroll-arrows'
        })}
        role={role}
      >
        <button
          type="button"
          className={classNames(styles.slideButton, styles.left)}
          onClick={() => {
            scrollRef.current?.scrollBy({
              left: -(listContainerRef.current?.scrollWidth ?? 0) + SCROLLBY_ADJUSTMENT,
              behavior: 'smooth'
            });
          }}
        >
          <Icon name="chevron-medium-left" className={styles.icon} size="lg" />
        </button>
        <div className={styles.containerInner} ref={scrollRef}>
          <ul className={styles.containerWrapper}>{children}</ul>
        </div>
        <button
          type="button"
          className={classNames(styles.slideButton, styles.right)}
          onClick={() => {
            scrollRef.current?.scrollBy({
              left: (listContainerRef.current?.scrollWidth ?? 0) - SCROLLBY_ADJUSTMENT,
              behavior: 'smooth'
            });
          }}
        >
          <Icon name="chevron-medium-right" className={styles.icon} size="lg" />
        </button>
      </Element>
    );
  }
);
